import {
  Dialog,
  Select,
  MenuItem,
  InputBase,
  Button,
  CircularProgress,
} from "@mui/material";
import { KeyboardArrowDown } from "@mui/icons-material";
import axios from "axios";
import posthog from "posthog-js";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import ForwardNumberUserField from "../reusables/ForwardNumberUserField";

const getInitialUserState = () => ({
  email: "",
  password: "1234",
  first_name: "",
  last_name: "",
  type: "user",
  forwarding_number: "",
  org_id: JSON.parse(localStorage.getItem("organization")).orgId.S,
  organization_name: JSON.parse(localStorage.getItem("organization"))
    .organization_name.S,
});

const AddUserModal = ({
  open,
  setOpen,
  setOpenUserInvitedModal,
  users,
  setUsers,
  planDetails,
  fetchUsers,
  loader,
  setLoader,
}) => {
  const navigate = useNavigate();

  const [errorMsg, setErrorMsg] = useState(null);

  const [user, setUser] = useState(getInitialUserState);

  const createUser = async () => {
    setLoader(true);

    try {
      const check = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}get-user-by-id`,
        {
          userId: JSON.parse(localStorage.getItem("user")).userId.S,
        }
      );

      const user___ = check.data.user;

      if (
        !user___.relatedOrgs.L?.find(
          (e) =>
            e?.M.org_id?.S ===
            JSON.parse(localStorage.getItem("organization")).orgId.S
        )
      ) {
        window.location.reload();
        return;
      }

      const res = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "user/create-user-without-org",
        user
      );

      const add_user = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "add-user",
        {
          user_id: res.data.user.userId.S,
          status: "pending",
          type: user.type,
          organization_id: user.org_id,
          justCreated: res.data.justCreated || false,
          ...(user.forwarding_number
            ? { forwarding_number: user.forwarding_number }
            : {}),
        }
      );

      localStorage.setItem(
        "organization",
        JSON.stringify(add_user.data.updatedItem)
      );

      setOpen(false);
      setUser(getInitialUserState());

      await axios.post(
        process.env.REACT_APP_BACKEND_URL + "email/send-invite",
        {
          organization_id: user.org_id || JSON.parse(localStorage.getItem("organization")).orgId.S,
          email: user.email,
          toName: user.first_name + " " + user.last_name,
          invite_url: `${process.env.REACT_APP_FRONTEND_URL}accept-invite/${add_user.data.encrypted_code}`,
          org_name: user.organization_name,
        }
      );

      setOpenUserInvitedModal(true);

      posthog.capture("Added a user", {
        email: JSON.parse(localStorage.getItem("user")).email.S,
        userId: JSON.parse(localStorage.getItem("user")).userId.S,
      });

      setUsers((prev) => [
        ...prev,
        {
          name: `${res.data.user.fName.S} ${res.data.user.lName.S}`,
          email: res.data.user.email.S,
          userType: user.type,
          status: "pending",
          user_id: res.data.user.userId.S,
          forwarding_number: user.forwarding_number || null,
        },
      ]);
      await fetchUsers();
    } catch (err) {
      setErrorMsg(err.response?.data?.message);
    }

    setLoader(false);
  };

  return (
    <Dialog
      open={open}
      fullWidth={false}
      maxWidth="md"
      style={{ borderRadius: "10px" }}
      BackdropProps={{
        style: { backgroundColor: "rgb(144,150,160,0.8)" },
      }}
    >
      <div className="px-6 py-4">
        {/* Title */}
        <div className="flex items-center justify-between  mb-3">
          <div className="text-[17px] font-semibold  ">Add New User</div>
          <X
            className="w-[18px] text-gray-400 cursor-pointer hover:text-gray-600"
            onClick={() => setOpen(false)}
          />
        </div>

        {/* Error */}
        {errorMsg && (
          <div className="text-red-600 text-sm mb-4">{errorMsg}</div>
        )}

        {/* User Type */}
        <div className="mb-4">
          <div className="text-[13px] font-semibold mb-2">User Type</div>
          <Select
            fullWidth
            IconComponent={KeyboardArrowDown}
            value={user.type}
            onChange={(e) => setUser({ ...user, type: e.target.value })}
            sx={{
              bgcolor: "white",
              border: "1px solid #D1D5DB",
              borderRadius: "6px",
              "& .MuiOutlinedInput-notchedOutline": { border: "none" },
              "& .MuiSelect-select": {
                padding: "12px",
                fontSize: "13px",
              },
            }}
          >
            <MenuItem value="user">
              User – Can make calls and view data
            </MenuItem>
            <MenuItem value="viewer">Viewer - Read access only </MenuItem>
            <MenuItem value="admin">
              Admin - Full access to all features
            </MenuItem>
          </Select>
        </div>

        {/* First + Last Name */}
        <div className="flex gap-4 mb-4">
          <div className="flex-1">
            <div className="text-[13px] font-semibold mb-2">First Name</div>
            <InputBase
              placeholder="First name"
              value={user.first_name}
              onChange={(e) => setUser({ ...user, first_name: e.target.value })}
              sx={{
                bgcolor: "#fff",
                border: "1px solid #D1D5DB",
                borderRadius: "6px",
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                px: 2,
                py: 1.2,
                width: "100%",
                borderRadius: "6px",
                fontSize: "13px",
              }}
            />
          </div>

          <div className="flex-1">
            <div className="text-[13px] font-semibold mb-2">Last Name</div>
            <InputBase
              placeholder="Last name"
              value={user.last_name}
              onChange={(e) => setUser({ ...user, last_name: e.target.value })}
              sx={{
                bgcolor: "#fff",
                border: "1px solid #D1D5DB",
                borderRadius: "6px",
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                px: 2,
                py: 1.2,
                width: "100%",
                borderRadius: "6px",
                fontSize: "13px",
              }}
            />
          </div>
        </div>

        {/* Email */}
        <div className="mb-4">
          <div className="text-[13px] font-semibold mb-2">Email Address</div>
          <InputBase
            placeholder="user@example.com"
            value={user.email}
            onChange={(e) => setUser({ ...user, email: e.target.value })}
            sx={{
              bgcolor: "#fff",
              border: "1px solid #D1D5DB",
              borderRadius: "6px",
              "& .MuiOutlinedInput-notchedOutline": { border: "none" },
              px: 2,
              py: 1.2,
              width: "100%",
              borderRadius: "6px",
              fontSize: "13px",
            }}
          />
        </div>

        <ForwardNumberUserField
          value={user.forwarding_number}
          onChange={(forwarding_number) =>
            setUser((prev) => ({ ...prev, forwarding_number }))
          }
          labelClassName="text-[13px] font-semibold"
          className="mb-6"
        />

        {/* Buttons */}
        <div className="flex justify-end gap-3">
          <Button
            onClick={() => setOpen(false)}
            sx={{
              textTransform: "none",
              bgcolor: "white",
              color: "#374151",
              border: "1px solid #F3F4F6",
              px: 4,
              py: 1,
              borderRadius: "8px",
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={createUser}
            disabled={loader}
            sx={{
              px: 1,
              py: 1,
              borderRadius: "8px",
            }}
          >
            {loader ? (
              <CircularProgress size="1.3rem" sx={{ color: "white" }} />
            ) : (
              "Save"
            )}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default AddUserModal;
